/**
 * /api/v1/DispatchActivityAttachment — file/photo attachments on a
 * dispatch activity. Multipart upload via FormData (matches the
 * Lead/ScaffoldTask attachment pattern in the rest of the app).
 */

import axios from 'axios';
import config from '../../../../../config';
import { unwrap } from '../http';
import type {
  BackendDispatchActivityAttachment,
  CreateUpdateResponse,
  DeleteResponse,
  PaginatedResponse,
  BatchCreateResponse,
  BatchDeleteResponse,
  BatchUpdateResponse,
  BatchUpdateRequestItem,
} from '../types';

const BASE = `${config.api.API_URL}/DispatchActivityAttachment`;

export const attachmentService = {
  getSingle: (id: string) =>
    axios.get<BackendDispatchActivityAttachment>(`${BASE}/${id}`).then(unwrap),

  /** All attachments for a given dispatch activity. Returns 204 if empty. */
  getByDispatchActivityId: (activityId: string) =>
    axios
      .get<BackendDispatchActivityAttachment[]>(`${BASE}/DispatchActivity/${activityId}`)
      .then(r => r.status === 204 ? [] : r.data),

  getAll: (pageNumber = 1, pageSize = 20) =>
    axios
      .get<PaginatedResponse<BackendDispatchActivityAttachment>>(BASE, {
        params: { pageNumber, pageSize },
      })
      .then(unwrap),

  search: (searchTerm: string, pageNumber = 1, pageSize = 20) =>
    axios
      .get<PaginatedResponse<BackendDispatchActivityAttachment>>(`${BASE}/Search`, {
        params: { searchTerm, pageNumber, pageSize },
      })
      .then(unwrap),

  /** Upload one file. Form fields: tenantId, dispatchActivityId, Attachment (file). */
  create: (tenantId: string, dispatchActivityId: string, file: File) => {
    const fd = new FormData();
    fd.append('TenantId', tenantId);
    fd.append('DispatchActivityId', dispatchActivityId);
    fd.append('Attachment', file);
    return axios
      .post<CreateUpdateResponse<BackendDispatchActivityAttachment>>(BASE, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(unwrap);
  },

  /** Upload many files in one request — backend treats as best-effort batch. */
  createBatch: (tenantId: string, dispatchActivityId: string, files: File[]) => {
    const fd = new FormData();
    fd.append('TenantId', tenantId);
    fd.append('DispatchActivityId', dispatchActivityId);
    files.forEach(f => fd.append('Attachments', f));
    return axios
      .post<BatchCreateResponse<BackendDispatchActivityAttachment>>(`${BASE}/CreateBatch`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(unwrap);
  },

  /** Replace an attachment with a new file (same row id, new blob). */
  update: (id: string, file: File) => {
    const fd = new FormData();
    fd.append('Attachment', file);
    return axios
      .put<CreateUpdateResponse<BackendDispatchActivityAttachment>>(`${BASE}/${id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(unwrap);
  },

  updateBatch: (items: BatchUpdateRequestItem<{ attachment?: File | null }>[]) =>
    axios
      .put<BatchUpdateResponse<BackendDispatchActivityAttachment>>(`${BASE}/UpdateBatch`, items)
      .then(unwrap),

  delete: (id: string) =>
    axios.delete<DeleteResponse>(`${BASE}/${id}`).then(unwrap),

  batchDelete: (ids: string[]) =>
    axios
      .post<BatchDeleteResponse>(`${BASE}/BatchDelete`, ids)
      .then(unwrap),
};
